import React, { useState, useEffect } from 'react';
import { adminApi } from '../../utils/api';
import { InfoTag } from '../../components/ui/InfoTag';
import { Save, Globe, Palette, Share2, RotateCcw, Moon, Sun, Key } from 'lucide-react';
import { motion } from 'framer-motion';
import {
  DARK_THEME_PRESET,
  LIGHT_THEME_PRESET,
  DEFAULT_THEME_SETTINGS,
  mergeThemeSettings,
} from '../../lib/themePresets';

// Maximum logo dimensions used during upload processing.
// This keeps quality high while still preventing extremely large payloads.
const LOGO_MAX_W = 2000;
const LOGO_MAX_H = 600;
// Target byte size for the stored data URL (200 KB). Above this the canvas
// progressively drops JPEG quality until the size fits.
const LOGO_MAX_BYTES = 200 * 1024;

const resizeAndCompressLogoFile = (file) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);

      // Compute scaled dimensions preserving aspect ratio.
      let w = img.naturalWidth || img.width;
      let h = img.naturalHeight || img.height;
      const ratio = Math.min(LOGO_MAX_W / w, LOGO_MAX_H / h, 1); // never upscale
      w = Math.round(w * ratio);
      h = Math.round(h * ratio);

      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, w, h);

      // SVGs or tiny PNGs stay as-is at full quality; large bitmaps get compressed.
      const isTransparent = file.type === 'image/png' || file.type === 'image/webp';
      const mimeOut = isTransparent ? 'image/png' : 'image/jpeg';

      if (isTransparent) {
        // For PNG/WEBP just return the resized image without quality loss.
        resolve(canvas.toDataURL(mimeOut));
        return;
      }

      // JPEG: drop quality until data URL fits in LOGO_MAX_BYTES.
      let quality = 0.88;
      let dataUrl = canvas.toDataURL(mimeOut, quality);
      while (dataUrl.length > LOGO_MAX_BYTES * 1.37 && quality > 0.40) {
        // base64 is ~1.37× the raw byte size
        quality = Math.round((quality - 0.10) * 100) / 100;
        dataUrl = canvas.toDataURL(mimeOut, quality);
      }
      resolve(dataUrl);
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Failed to load image for resizing'));
    };

    img.src = objectUrl;
  });

const DEFAULT_AI_SETTINGS = {
  default_provider: 'anthropic',
  fallback_order: ['openai', 'gemini', 'grok', 'ollama'],
  model_map: {
    anthropic: 'claude-3-5-sonnet-20241022',
    openai: 'gpt-4o-mini',
    gemini: 'gemini-1.5-flash',
    grok: 'grok-2-latest',
    ollama: 'llama3.1:8b',
  },
  ollama_base_url: 'http://127.0.0.1:11434',
  key_status: {
    anthropic: { configured: false, masked: '' },
    openai: { configured: false, masked: '' },
    gemini: { configured: false, masked: '' },
    grok: { configured: false, masked: '' },
  },
};

const DEFAULT_OPS_CONFIG = {
  convertkit_api_key: '',
  convertkit_api_secret: '',
  buffer_access_token: '',
  make_webhook_url: '',
  resend_api_key: '',
  resend_from_email: 'marketing@wowrolla.ca',
  google_client_id: '',
  google_client_secret: '',
  google_redirect_uri: '',
  microsoft_client_id: '',
  microsoft_client_secret: '',
  microsoft_redirect_uri: '',
  default_timezone: 'UTC',
  consulting_leads_email: 'strategy@wowrolla.ca',
  default_partner_manager_email: 'partners@wowrolla.ca',
};

const statusTone = {
  configured: { color: '#0b0', bg: 'rgba(0,180,0,0.12)' },
  missing: { color: '#ff8a00', bg: 'rgba(255,138,0,0.12)' },
  active: { color: '#0b0', bg: 'rgba(0,180,0,0.12)' },
};

const StatusPill = ({ status }) => {
  const tone = statusTone[status] || { color: 'var(--text-muted)', bg: 'rgba(255,255,255,0.06)' };
  return (
    <span className="px-2 py-1 text-xs uppercase font-mono" style={{ color: tone.color, background: tone.bg, border: '1px solid var(--border-subtle)' }}>
      {status || 'unknown'}
    </span>
  );
};

const COLOR_FIELDS = [
  { key: 'primary_color', label: 'Primary Accent' },
  { key: 'secondary_color', label: 'Secondary Accent' },
  { key: 'accent_highlight', label: 'Highlight Accent' },
  { key: 'glow_1', label: 'Neon Accent 1' },
  { key: 'glow_2', label: 'Neon Accent 2' },
  { key: 'glow_3', label: 'Neon Accent 3' },
  { key: 'glow_4', label: 'Neon Accent 4' },
  { key: 'glow_5', label: 'Neon Accent 5' },
  { key: 'glow_6', label: 'Neon Accent 6' },
  { key: 'bg_base', label: 'Background Base' },
  { key: 'bg_surface', label: 'Background Surface' },
  { key: 'bg_elevated', label: 'Background Elevated' },
  { key: 'dropdown_bg', label: 'Dropdown Background' },
  { key: 'dropdown_text', label: 'Dropdown Text' },
  { key: 'dropdown_hover_bg', label: 'Dropdown Hover' },
  { key: 'text_primary', label: 'Text Primary' },
  { key: 'text_secondary', label: 'Text Secondary' },
  { key: 'text_muted', label: 'Text Muted' },
];

const BORDER_FIELDS = [
  { key: 'border_subtle', label: 'Border Subtle (rgba)' },
  { key: 'border_strong', label: 'Border Strong (rgba)' },
  { key: 'button_radius', label: 'Button Radius' },
];

function ThemeEditorSection({ title, icon: Icon, accentClass, themeKey, themeValues, onThemeChange, onRestorePreset }) {
  const previewStyle = {
    backgroundColor: themeValues.bg_surface,
    color: themeValues.text_primary,
    border: `1px solid ${themeValues.border_subtle}`,
    borderRadius: themeValues.button_radius,
  };

  return (
    <div className="terminal-grid p-6">
      <div className="flex items-center justify-between gap-4 mb-5">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <Icon size={18} className={accentClass} /> {title}
        </h3>
        <button
          type="button"
          onClick={onRestorePreset}
          className="inline-flex items-center gap-2 px-3 py-2 text-xs font-semibold rounded-lg border"
          style={{ borderColor: 'var(--border-subtle)', color: 'var(--text-secondary)' }}
        >
          <RotateCcw size={14} /> Restore Preset
        </button>
      </div>

      <p className="text-sm mb-5" style={{ color: 'var(--text-secondary)' }}>
        Optimized default palette for {themeKey === 'dark_theme' ? 'dark mode' : 'light mode'} with comfortable contrast, restrained accents, and cleaner long-session readability.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {COLOR_FIELDS.map(({ key, label }) => (
          <div key={`${themeKey}-${key}`}>
            <label className="block text-text-secondary text-sm mb-2">{label}</label>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={themeValues[key]}
                onChange={(e) => onThemeChange(key, e.target.value)}
                className="w-12 h-12 bg-transparent border border-white/20 cursor-pointer rounded"
              />
              <input
                type="text"
                value={themeValues[key]}
                onChange={(e) => onThemeChange(key, e.target.value)}
                className="input-pro flex-1 font-mono"
              />
            </div>
          </div>
        ))}

        {BORDER_FIELDS.map(({ key, label }) => (
          <div key={`${themeKey}-${key}`}>
            <label className="block text-text-secondary text-sm mb-2">{label}</label>
            <input
              type="text"
              value={themeValues[key]}
              onChange={(e) => onThemeChange(key, e.target.value)}
              className="input-pro font-mono"
              placeholder={key === 'button_radius' ? '12px or 9999px' : 'rgba(...)'}
            />
          </div>
        ))}
      </div>

      <div className="mt-6 p-4" style={previewStyle}>
        <p className="text-sm mb-3" style={{ color: themeValues.text_secondary }}>Preset Preview</p>
        <div className="flex flex-wrap items-center gap-3 mb-3">
          <button
            type="button"
            className="px-4 py-2 font-semibold"
            style={{
              background: themeValues.primary_color,
              color: themeValues.bg_base,
              borderRadius: themeValues.button_radius,
            }}
          >
            Primary Button
          </button>
          <button
            type="button"
            className="px-4 py-2 font-semibold"
            style={{
              border: `1px solid ${themeValues.border_strong}`,
              color: themeValues.text_primary,
              borderRadius: themeValues.button_radius,
              background: themeValues.bg_elevated,
            }}
          >
            Secondary Button
          </button>
          <span
            className="px-3 py-1 text-xs font-semibold"
            style={{ background: themeValues.accent_highlight, color: themeValues.bg_base, borderRadius: themeValues.button_radius }}
          >
            Label
          </span>
        </div>
        <input
          type="text"
          readOnly
          value="Input preview"
          className="w-full px-3 py-2"
          style={{
            background: themeValues.bg_base,
            border: `1px solid ${themeValues.border_subtle}`,
            color: themeValues.text_primary,
            borderRadius: themeValues.button_radius,
          }}
        />
      </div>
    </div>
  );
}

const SiteSettingsPage = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [aiSettings, setAiSettings] = useState(DEFAULT_AI_SETTINGS);
  const [aiKeysInput, setAiKeysInput] = useState({ anthropic: '', openai: '', gemini: '', grok: '' });
  const [aiSaving, setAiSaving] = useState(false);
  const [aiSaved, setAiSaved] = useState(false);
  const [opsConfig, setOpsConfig] = useState(DEFAULT_OPS_CONFIG);
  const [opsSaving, setOpsSaving] = useState(false);
  const [opsSaved, setOpsSaved] = useState(false);
  const [diagnostics, setDiagnostics] = useState([]);
  const [testingDiagnostics, setTestingDiagnostics] = useState(false);

  useEffect(() => {
    loadSettings();
    loadOpsConfig();
  }, []);

  const loadOpsConfig = async () => {
    try {
      const response = await adminApi.getOpsConfig();
      setOpsConfig({ ...DEFAULT_OPS_CONFIG, ...(response.data || {}) });
    } catch (error) {
      console.error('Failed to load integration config:', error);
    }
  };

  const saveOpsConfig = async () => {
    setOpsSaving(true);
    try {
      await adminApi.updateOpsConfig(opsConfig);
      setOpsSaved(true);
      setTimeout(() => setOpsSaved(false), 2000);
      await runDiagnostics();
    } catch (error) {
      alert('Failed to save integration config');
    } finally {
      setOpsSaving(false);
    }
  };

  const runDiagnostics = async () => {
    setTestingDiagnostics(true);
    try {
      const response = await adminApi.validateOpsConfig();
      setDiagnostics(response.data?.checks || []);
    } catch (error) {
      alert('Failed to run integration diagnostics');
    } finally {
      setTestingDiagnostics(false);
    }
  };

  const loadSettings = async () => {
    try {
      const [res, aiRes] = await Promise.all([
        adminApi.getSettings(),
        adminApi.getAiSettings().catch(() => ({ data: DEFAULT_AI_SETTINGS })),
      ]);
      const apiSettings = res.data || {};
      const cached = localStorage.getItem('wowrolla-site-settings');
      const cachedSettings = cached ? mergeThemeSettings(JSON.parse(cached)) : null;
      const mergedApi = mergeThemeSettings(apiSettings);
      const merged = {
        ...mergedApi,
        dark_theme: apiSettings.dark_theme || cachedSettings?.dark_theme || mergedApi.dark_theme,
        light_theme: apiSettings.light_theme || cachedSettings?.light_theme || mergedApi.light_theme,
      };
      setSettings(merged);
      const aiData = aiRes?.data || DEFAULT_AI_SETTINGS;
      setAiSettings({
        ...DEFAULT_AI_SETTINGS,
        ...aiData,
        model_map: { ...DEFAULT_AI_SETTINGS.model_map, ...(aiData.model_map || {}) },
        key_status: { ...DEFAULT_AI_SETTINGS.key_status, ...(aiData.key_status || {}) },
      });
    } catch (error) {
      console.error('Failed to load settings:', error);
      setSettings(mergeThemeSettings({
        site_name: 'Wowrolla',
        site_tagline: 'Your AI Arsenal Hub',
        logo_url: '',
        favicon_url: '',
        footer_text: '',
        social_links: {},
        ga_tracking_id: '',
        meta_defaults: {},
      }));
      setAiSettings(DEFAULT_AI_SETTINGS);
    } finally {
      setLoading(false);
    }
  };

  const buildPayload = (sourceSettings) => {
    const merged = mergeThemeSettings(sourceSettings || {});
    return {
      ...merged,
      primary_color: merged.dark_theme.primary_color,
      secondary_color: merged.dark_theme.secondary_color,
      accent_highlight: merged.dark_theme.accent_highlight,
      glow_1: merged.dark_theme.glow_1,
      glow_2: merged.dark_theme.glow_2,
      glow_3: merged.dark_theme.glow_3,
      glow_4: merged.dark_theme.glow_4,
      glow_5: merged.dark_theme.glow_5,
      glow_6: merged.dark_theme.glow_6,
      bg_base: merged.dark_theme.bg_base,
      bg_surface: merged.dark_theme.bg_surface,
      bg_elevated: merged.dark_theme.bg_elevated,
      dropdown_bg: merged.dark_theme.dropdown_bg,
      dropdown_text: merged.dark_theme.dropdown_text,
      dropdown_hover_bg: merged.dark_theme.dropdown_hover_bg,
      text_primary: merged.dark_theme.text_primary,
      text_secondary: merged.dark_theme.text_secondary,
      text_muted: merged.dark_theme.text_muted,
      border_subtle: merged.dark_theme.border_subtle,
      border_strong: merged.dark_theme.border_strong,
      button_radius: merged.dark_theme.button_radius,
    };
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = buildPayload(settings);
      await adminApi.updateSettings(payload);
      localStorage.setItem('wowrolla-site-settings', JSON.stringify(payload));
      setSettings(payload);
      window.dispatchEvent(new CustomEvent('wowrolla-theme-settings-updated', { detail: payload }));
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (error) {
      alert('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const updateSocial = (platform, url) => {
    setSettings((prev) => ({
      ...prev,
      social_links: { ...prev.social_links, [platform]: url },
    }));
  };

  const updateMeta = (key, value) => {
    setSettings((prev) => ({
      ...prev,
      meta_defaults: { ...prev.meta_defaults, [key]: value },
    }));
  };

  const updateThemeValue = (themeKey, key, value) => {
    setSettings((prev) => ({
      ...prev,
      [themeKey]: {
        ...prev[themeKey],
        [key]: value,
      },
    }));
  };

  const restorePreset = (themeKey) => {
    const preset = themeKey === 'dark_theme' ? DARK_THEME_PRESET : LIGHT_THEME_PRESET;
    setSettings((prev) => ({
      ...prev,
      [themeKey]: { ...preset },
    }));
  };

  const updateAiModel = (provider, value) => {
    setAiSettings((prev) => ({
      ...prev,
      model_map: {
        ...prev.model_map,
        [provider]: value,
      },
    }));
  };

  const setAiProvider = (provider) => {
    setAiSettings((prev) => ({ ...prev, default_provider: provider }));
  };

  const setAiFallbackOrderFromText = (value) => {
    const parsed = value
      .split(',')
      .map((item) => item.trim().toLowerCase())
      .filter(Boolean);
    setAiSettings((prev) => ({ ...prev, fallback_order: parsed }));
  };

  const saveAiSettings = async () => {
    setAiSaving(true);
    try {
      const payload = {
        default_provider: aiSettings.default_provider,
        fallback_order: aiSettings.fallback_order,
        model_map: aiSettings.model_map,
        ollama_base_url: aiSettings.ollama_base_url,
        api_keys: {
          anthropic: aiKeysInput.anthropic,
          openai: aiKeysInput.openai,
          gemini: aiKeysInput.gemini,
          grok: aiKeysInput.grok,
        },
      };
      const response = await adminApi.updateAiSettings(payload);
      const aiData = response?.data || {};
      setAiSettings((prev) => ({
        ...prev,
        ...aiData,
        model_map: { ...prev.model_map, ...(aiData.model_map || {}) },
        key_status: { ...prev.key_status, ...(aiData.key_status || {}) },
      }));
      setAiKeysInput({ anthropic: '', openai: '', gemini: '', grok: '' });
      setAiSaved(true);
      setTimeout(() => setAiSaved(false), 2000);
    } catch (error) {
      alert('Failed to save AI provider settings');
    } finally {
      setAiSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-cyber-yellow"></div></div>;
  }

  if (!settings) return null;

  const mergedSettings = mergeThemeSettings(settings);

  const handleLogoFileSelect = async (targetField, event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;

    if (!String(file.type || '').startsWith('image/')) {
      alert('Please choose an image file (PNG, JPG, SVG, WEBP, etc.).');
      event.target.value = '';
      return;
    }

    try {
      const dataUrl = await resizeAndCompressLogoFile(file);
      setSettings({ ...mergedSettings, [targetField]: dataUrl });
    } catch (error) {
      alert('Unable to load selected image file.');
    } finally {
      event.target.value = '';
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} data-testid="site-settings">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-3xl font-bold text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>Site Settings</h2>
            <InfoTag label="Global configuration" description="Manage site-wide settings including branding, theme colors, SEO defaults, and social metadata" variant="primary" />
          </div>
          <p className="text-sm mt-2" style={{ color: 'var(--text-secondary)' }}>
            Separate preset-ready palettes for dark and light mode. Admin can restore the UX-safe defaults at any time.
          </p>
        </div>
        <button onClick={handleSave} disabled={saving}
          className={`btn-primary inline-flex items-center gap-2 ${saved ? '!bg-laser-green' : ''}`}
          data-testid="save-settings-button">
          <Save size={16} />
          {saved ? 'Saved!' : saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>

      <div className="space-y-6">
        <div className="terminal-grid p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Globe size={18} className="text-cyber-yellow" /> General
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-text-secondary text-sm mb-2">Site Name</label>
              <input type="text" value={mergedSettings.site_name}
                onChange={(e) => setSettings({ ...mergedSettings, site_name: e.target.value })}
                className="input-pro"
                data-testid="setting-site-name" />
            </div>
            <div>
              <label className="block text-text-secondary text-sm mb-2">Tagline</label>
              <input type="text" value={mergedSettings.site_tagline}
                onChange={(e) => setSettings({ ...mergedSettings, site_tagline: e.target.value })}
                className="input-pro" />
            </div>
            <div>
              <label className="block text-text-secondary text-sm mb-2">Logo URL</label>
              <input type="url" value={mergedSettings.logo_url || ''}
                onChange={(e) => setSettings({ ...mergedSettings, logo_url: e.target.value })}
                className="input-pro" />
            </div>
            <div className="md:col-span-2">
              <label className="flex text-text-secondary text-sm mb-3 items-center gap-2">
                <Moon size={14} className="text-cyber-yellow" /> Dark Theme Logo
              </label>
              <div className="flex flex-col gap-2">
                <input
                  type="url"
                  value={mergedSettings.logo_dark_url || ''}
                  onChange={(e) => setSettings({ ...mergedSettings, logo_dark_url: e.target.value })}
                  className="input-pro"
                  placeholder="Paste a URL, or use the file picker below"
                />
                <label
                  className="inline-flex items-center gap-2 cursor-pointer px-3 py-2 text-sm font-medium rounded-lg border w-fit"
                  style={{ borderColor: 'var(--border-subtle)', color: 'var(--text-secondary)', background: 'var(--bg-elevated)' }}
                >
                  Choose image file (auto-resized to fit header)
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleLogoFileSelect('logo_dark_url', e)}
                  />
                </label>
                {mergedSettings.logo_dark_url && (
                  <div
                    className="mt-1 px-3 py-2 rounded flex items-center gap-3"
                    style={{ background: '#0B1020', border: '1px solid var(--border-subtle)' }}
                  >
                    <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Preview (dark):</span>
                    <img
                      src={mergedSettings.logo_dark_url}
                      alt="Dark logo preview"
                      style={{ height: '40px', width: 'auto', maxWidth: '240px', objectFit: 'contain' }}
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                    <button
                      type="button"
                      onClick={() => setSettings({ ...mergedSettings, logo_dark_url: '' })}
                      className="ml-auto text-xs px-2 py-1 rounded"
                      style={{ color: 'var(--text-muted)', border: '1px solid var(--border-subtle)' }}
                    >Clear</button>
                  </div>
                )}
              </div>
            </div>
            <div className="md:col-span-2">
              <label className="flex text-text-secondary text-sm mb-3 items-center gap-2">
                <Sun size={14} className="text-laser-green" /> Light Theme Logo
              </label>
              <div className="flex flex-col gap-2">
                <input
                  type="url"
                  value={mergedSettings.logo_light_url || ''}
                  onChange={(e) => setSettings({ ...mergedSettings, logo_light_url: e.target.value })}
                  className="input-pro"
                  placeholder="Paste a URL, or use the file picker below"
                />
                <label
                  className="inline-flex items-center gap-2 cursor-pointer px-3 py-2 text-sm font-medium rounded-lg border w-fit"
                  style={{ borderColor: 'var(--border-subtle)', color: 'var(--text-secondary)', background: 'var(--bg-elevated)' }}
                >
                  Choose image file (auto-resized to fit header)
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleLogoFileSelect('logo_light_url', e)}
                  />
                </label>
                {mergedSettings.logo_light_url && (
                  <div
                    className="mt-1 px-3 py-2 rounded flex items-center gap-3"
                    style={{ background: '#F6F8FB', border: '1px solid var(--border-subtle)' }}
                  >
                    <span className="text-xs" style={{ color: '#64748B' }}>Preview (light):</span>
                    <img
                      src={mergedSettings.logo_light_url}
                      alt="Light logo preview"
                      style={{ height: '40px', width: 'auto', maxWidth: '240px', objectFit: 'contain' }}
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                    <button
                      type="button"
                      onClick={() => setSettings({ ...mergedSettings, logo_light_url: '' })}
                      className="ml-auto text-xs px-2 py-1 rounded"
                      style={{ color: '#64748B', border: '1px solid rgba(15,23,42,0.18)' }}
                    >Clear</button>
                  </div>
                )}
              </div>
            </div>
            <div>
              <label className="block text-text-secondary text-sm mb-2">Favicon URL</label>
              <input type="url" value={mergedSettings.favicon_url || ''}
                onChange={(e) => setSettings({ ...mergedSettings, favicon_url: e.target.value })}
                className="input-pro" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-text-secondary text-sm mb-2">Footer Text</label>
              <input type="text" value={mergedSettings.footer_text || ''}
                onChange={(e) => setSettings({ ...mergedSettings, footer_text: e.target.value })}
                className="input-pro" />
            </div>
          </div>
        </div>

        <div className="terminal-grid p-6" data-testid="site-integration-settings">
          <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Key size={18} className="text-cyber-yellow" /> Integration Settings
                </h3>
                <InfoTag label="API credentials for integrations" description="Store third-party API keys for ConvertKit, Buffer, Make, Resend, Google OAuth, and Microsoft OAuth" variant="primary" />
              </div>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                This replaces Newsletter tab integration config. Manage all integration credentials from Settings.
              </p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <button type="button" onClick={runDiagnostics} disabled={testingDiagnostics} className="btn-secondary">
                {testingDiagnostics ? 'Testing...' : 'Run Diagnostics'}
              </button>
              <button
                type="button"
                onClick={saveOpsConfig}
                disabled={opsSaving}
                className={`btn-primary inline-flex items-center gap-2 ${opsSaved ? '!bg-laser-green' : ''}`}
              >
                <Save size={14} />
                {opsSaved ? 'Saved!' : opsSaving ? 'Saving...' : 'Save Integration Config'}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-text-secondary text-sm mb-2">ConvertKit API Key</label>
              <input type="text" value={opsConfig.convertkit_api_key} onChange={(e) => setOpsConfig((prev) => ({ ...prev, convertkit_api_key: e.target.value }))} className="input-pro" />
            </div>
            <div>
              <label className="block text-text-secondary text-sm mb-2">ConvertKit API Secret</label>
              <input type="password" value={opsConfig.convertkit_api_secret} onChange={(e) => setOpsConfig((prev) => ({ ...prev, convertkit_api_secret: e.target.value }))} className="input-pro" />
            </div>
            <div>
              <label className="block text-text-secondary text-sm mb-2">Buffer Access Token</label>
              <input type="password" value={opsConfig.buffer_access_token} onChange={(e) => setOpsConfig((prev) => ({ ...prev, buffer_access_token: e.target.value }))} className="input-pro" />
            </div>
            <div>
              <label className="block text-text-secondary text-sm mb-2">Make Webhook URL</label>
              <input type="url" value={opsConfig.make_webhook_url} onChange={(e) => setOpsConfig((prev) => ({ ...prev, make_webhook_url: e.target.value }))} className="input-pro" />
            </div>
            <div>
              <label className="block text-text-secondary text-sm mb-2">Resend API Key</label>
              <input type="password" value={opsConfig.resend_api_key} onChange={(e) => setOpsConfig((prev) => ({ ...prev, resend_api_key: e.target.value }))} className="input-pro" />
            </div>
            <div>
              <label className="block text-text-secondary text-sm mb-2">Resend From Email</label>
              <input type="email" value={opsConfig.resend_from_email} onChange={(e) => setOpsConfig((prev) => ({ ...prev, resend_from_email: e.target.value }))} className="input-pro" />
            </div>
            <div>
              <label className="block text-text-secondary text-sm mb-2">Google Client ID</label>
              <input type="text" value={opsConfig.google_client_id} onChange={(e) => setOpsConfig((prev) => ({ ...prev, google_client_id: e.target.value }))} className="input-pro" />
            </div>
            <div>
              <label className="block text-text-secondary text-sm mb-2">Google Client Secret</label>
              <input type="password" value={opsConfig.google_client_secret} onChange={(e) => setOpsConfig((prev) => ({ ...prev, google_client_secret: e.target.value }))} className="input-pro" />
            </div>
            <div>
              <label className="block text-text-secondary text-sm mb-2">Google Redirect URI</label>
              <input type="url" value={opsConfig.google_redirect_uri} onChange={(e) => setOpsConfig((prev) => ({ ...prev, google_redirect_uri: e.target.value }))} className="input-pro" />
            </div>
            <div>
              <label className="block text-text-secondary text-sm mb-2">Microsoft Client ID</label>
              <input type="text" value={opsConfig.microsoft_client_id} onChange={(e) => setOpsConfig((prev) => ({ ...prev, microsoft_client_id: e.target.value }))} className="input-pro" />
            </div>
            <div>
              <label className="block text-text-secondary text-sm mb-2">Microsoft Client Secret</label>
              <input type="password" value={opsConfig.microsoft_client_secret} onChange={(e) => setOpsConfig((prev) => ({ ...prev, microsoft_client_secret: e.target.value }))} className="input-pro" />
            </div>
            <div>
              <label className="block text-text-secondary text-sm mb-2">Microsoft Redirect URI</label>
              <input type="url" value={opsConfig.microsoft_redirect_uri} onChange={(e) => setOpsConfig((prev) => ({ ...prev, microsoft_redirect_uri: e.target.value }))} className="input-pro" />
            </div>
            <div>
              <label className="block text-text-secondary text-sm mb-2">Default Timezone</label>
              <input type="text" value={opsConfig.default_timezone} onChange={(e) => setOpsConfig((prev) => ({ ...prev, default_timezone: e.target.value }))} className="input-pro" />
            </div>
            <div>
              <label className="block text-text-secondary text-sm mb-2">Consulting Leads Email</label>
              <input type="email" value={opsConfig.consulting_leads_email} onChange={(e) => setOpsConfig((prev) => ({ ...prev, consulting_leads_email: e.target.value }))} className="input-pro" />
            </div>
            <div>
              <label className="block text-text-secondary text-sm mb-2">Default Partner Manager Email</label>
              <input type="email" value={opsConfig.default_partner_manager_email} onChange={(e) => setOpsConfig((prev) => ({ ...prev, default_partner_manager_email: e.target.value }))} className="input-pro" />
            </div>
          </div>

          {diagnostics.length > 0 && (
            <div className="mt-6">
              <p className="text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Connectivity status</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {diagnostics.map((item) => (
                  <div key={item.name} className="p-3" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}>
                    <div className="flex items-center justify-between gap-3 mb-1">
                      <span className="font-semibold text-white">{item.name}</span>
                      <StatusPill status={item.status} />
                    </div>
                    <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{item.detail}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="terminal-grid p-6" data-testid="ai-runtime-settings">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Globe size={18} className="text-laser-green" /> AI Provider Fallback
            </h3>
            <button
              type="button"
              onClick={saveAiSettings}
              disabled={aiSaving}
              className={`btn-primary inline-flex items-center gap-2 ${aiSaved ? '!bg-laser-green' : ''}`}
            >
              <Save size={14} />
              {aiSaved ? 'Saved!' : aiSaving ? 'Saving...' : 'Save AI Settings'}
            </button>
          </div>
          <p className="text-sm mb-5" style={{ color: 'var(--text-secondary)' }}>
            Configure the AI provider order used for document/content generation. If one provider fails (credits, quota, outage), Wowrolla will try the next provider automatically.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-text-secondary text-sm mb-2">Default Provider</label>
              <select
                value={aiSettings.default_provider}
                onChange={(e) => setAiProvider(e.target.value)}
                className="input-pro"
              >
                <option value="anthropic">Anthropic (Claude API)</option>
                <option value="openai">OpenAI</option>
                <option value="gemini">Google Gemini</option>
                <option value="grok">xAI Grok</option>
                <option value="ollama">Local Ollama</option>
              </select>
            </div>

            <div>
              <label className="block text-text-secondary text-sm mb-2">Fallback Order (comma-separated)</label>
              <input
                type="text"
                value={(aiSettings.fallback_order || []).join(', ')}
                onChange={(e) => setAiFallbackOrderFromText(e.target.value)}
                className="input-pro"
                placeholder="openai, gemini, grok, ollama"
              />
            </div>

            <div>
              <label className="block text-text-secondary text-sm mb-2">Anthropic Model</label>
              <input
                type="text"
                value={aiSettings.model_map?.anthropic || ''}
                onChange={(e) => updateAiModel('anthropic', e.target.value)}
                className="input-pro"
                placeholder="claude-3-5-sonnet-20241022"
              />
            </div>

            <div>
              <label className="block text-text-secondary text-sm mb-2">OpenAI Model</label>
              <input
                type="text"
                value={aiSettings.model_map?.openai || ''}
                onChange={(e) => updateAiModel('openai', e.target.value)}
                className="input-pro"
                placeholder="gpt-4o-mini"
              />
            </div>

            <div>
              <label className="block text-text-secondary text-sm mb-2">Gemini Model</label>
              <input
                type="text"
                value={aiSettings.model_map?.gemini || ''}
                onChange={(e) => updateAiModel('gemini', e.target.value)}
                className="input-pro"
                placeholder="gemini-1.5-flash"
              />
            </div>

            <div>
              <label className="block text-text-secondary text-sm mb-2">Grok Model</label>
              <input
                type="text"
                value={aiSettings.model_map?.grok || ''}
                onChange={(e) => updateAiModel('grok', e.target.value)}
                className="input-pro"
                placeholder="grok-2-latest"
              />
            </div>

            <div>
              <label className="block text-text-secondary text-sm mb-2">Ollama Model</label>
              <input
                type="text"
                value={aiSettings.model_map?.ollama || ''}
                onChange={(e) => updateAiModel('ollama', e.target.value)}
                className="input-pro"
                placeholder="llama3.1:8b"
              />
            </div>

            <div>
              <label className="block text-text-secondary text-sm mb-2">Ollama Base URL</label>
              <input
                type="url"
                value={aiSettings.ollama_base_url || ''}
                onChange={(e) => setAiSettings((prev) => ({ ...prev, ollama_base_url: e.target.value }))}
                className="input-pro"
                placeholder="http://127.0.0.1:11434"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
            <div>
              <label className="block text-text-secondary text-sm mb-2">Anthropic API Key</label>
              <input
                type="password"
                value={aiKeysInput.anthropic}
                onChange={(e) => setAiKeysInput((prev) => ({ ...prev, anthropic: e.target.value }))}
                className="input-pro"
                placeholder={aiSettings.key_status?.anthropic?.configured ? `Configured (${aiSettings.key_status?.anthropic?.masked})` : 'Paste key or leave blank'}
              />
            </div>
            <div>
              <label className="block text-text-secondary text-sm mb-2">OpenAI API Key</label>
              <input
                type="password"
                value={aiKeysInput.openai}
                onChange={(e) => setAiKeysInput((prev) => ({ ...prev, openai: e.target.value }))}
                className="input-pro"
                placeholder={aiSettings.key_status?.openai?.configured ? `Configured (${aiSettings.key_status?.openai?.masked})` : 'Paste key or leave blank'}
              />
            </div>
            <div>
              <label className="block text-text-secondary text-sm mb-2">Gemini API Key</label>
              <input
                type="password"
                value={aiKeysInput.gemini}
                onChange={(e) => setAiKeysInput((prev) => ({ ...prev, gemini: e.target.value }))}
                className="input-pro"
                placeholder={aiSettings.key_status?.gemini?.configured ? `Configured (${aiSettings.key_status?.gemini?.masked})` : 'Paste key or leave blank'}
              />
            </div>
            <div>
              <label className="block text-text-secondary text-sm mb-2">Grok API Key</label>
              <input
                type="password"
                value={aiKeysInput.grok}
                onChange={(e) => setAiKeysInput((prev) => ({ ...prev, grok: e.target.value }))}
                className="input-pro"
                placeholder={aiSettings.key_status?.grok?.configured ? `Configured (${aiSettings.key_status?.grok?.masked})` : 'Paste key or leave blank'}
              />
            </div>
          </div>
        </div>

        <ThemeEditorSection
          title="Dark Theme Preset"
          icon={Moon}
          accentClass="text-cyber-yellow"
          themeKey="dark_theme"
          themeValues={mergedSettings.dark_theme}
          onThemeChange={(key, value) => updateThemeValue('dark_theme', key, value)}
          onRestorePreset={() => restorePreset('dark_theme')}
        />

        <ThemeEditorSection
          title="Light Theme Preset"
          icon={Sun}
          accentClass="text-laser-green"
          themeKey="light_theme"
          themeValues={mergedSettings.light_theme}
          onThemeChange={(key, value) => updateThemeValue('light_theme', key, value)}
          onRestorePreset={() => restorePreset('light_theme')}
        />

        <div className="terminal-grid p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Share2 size={18} className="text-purple-400" /> Social Links
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {['twitter', 'youtube', 'linkedin', 'instagram', 'tiktok', 'github'].map((platform) => (
              <div key={platform}>
                <label className="block text-text-secondary text-sm mb-2 capitalize">{platform}</label>
                <input type="url" value={mergedSettings.social_links?.[platform] || ''}
                  onChange={(e) => updateSocial(platform, e.target.value)}
                  className="input-pro"
                  placeholder={`https://${platform}.com/...`} />
              </div>
            ))}
          </div>
        </div>

        <div className="terminal-grid p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Globe size={18} className="text-blue-400" /> SEO Defaults
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-text-secondary text-sm mb-2">Default Meta Title</label>
              <input type="text" value={mergedSettings.meta_defaults?.title || ''}
                onChange={(e) => updateMeta('title', e.target.value)}
                className="input-pro"
                placeholder="Wowrolla - Discover the Best AI Tools" />
            </div>
            <div>
              <label className="block text-text-secondary text-sm mb-2">Default Meta Description</label>
              <textarea value={mergedSettings.meta_defaults?.description || ''}
                onChange={(e) => updateMeta('description', e.target.value)}
                className="input-pro"
                rows="3" placeholder="Curated directory of AI tools..." />
            </div>
            <div>
              <label className="block text-text-secondary text-sm mb-2">Google Analytics Tracking ID</label>
              <input type="text" value={mergedSettings.ga_tracking_id || ''}
                onChange={(e) => setSettings({ ...mergedSettings, ga_tracking_id: e.target.value })}
                className="input-pro"
                placeholder="G-XXXXXXXXXX" data-testid="setting-ga-id" />
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default SiteSettingsPage;

